import { CloseOutlined, FilterAltOffOutlined, FilterAltOutlined } from "@mui/icons-material";
import { Alert, Box, Button, Card, Grid, IconButton, TextField, Typography, useMediaQuery, useTheme } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { GET } from "../../../Utilities/BaseService";
import { MultiPermissionAuthorize } from "../../../Utilities/MultiPermissionAuthorize";
import UpdatedSearchableInput from "../../../Utilities/UpdatedSearchableInput";
import BaseTable from "../../Table/BaseTable";
import SearchBar from "../../Table/Components/SearchBar";
import { monthFormatter } from "../Bill/SingleBill";

type Filter = {
    search: string;
    district: number | "" | "all";
    village: number | "" | "all";
    hamlet: number | "" | "all";
    powerstation: string;
    month: any;
    year: any;
};

const initialFilter: Filter = {
    search: "",
    district: "",
    village: "",
    hamlet: "",
    powerstation: "",
    month: "",
    year: "",
};

const initialClearAll = {
    district: false,
    village: false,
    hamlet: false,
    powerstation: false,
    search: false,
};

export const getPrintBillHistory = (pagination: any, filter: any) => {
    return GET("/reading/print-bill-history", {
        meterNo: filter.search ? filter.search : undefined,
        district: filter.district && filter.district !== "all" ? filter.district : undefined,
        village: filter.village && filter.village !== "all" ? filter.village : undefined,
        hamlet: filter.hamlet && filter.hamlet !== "all" ? filter.hamlet : undefined,
        powerstation: filter.powerstation ? filter.powerstation : undefined,
        month: filter.month,
        year: filter.year,
        page: pagination.page + 1,
        limit: pagination.limit,
    });
};

const PrintBillHistory = () => {
    const currentMonth = new Date().toISOString().slice(0, 7);
    const [pagination, setPagination] = useState<any>({ page: 0, limit: 100 });
    const [billingMonth, setBillingMonth] = useState("");
    const [clearAll, setClearAll] = useState(initialClearAll);
    const [filter, setFilter] = useState<any>(initialFilter);
    const [feedback, setFeedback] = useState("");
    const [rowsCount, setRowsCount] = useState(0);
    const [changeCount, setChangeCount] = useState(0);
    const theme = useTheme();
    const mdDown = useMediaQuery(theme.breakpoints.down("md"));

    const handleClearAll = () => {
        setFilter(initialFilter);
        setClearAll({
            district: true,
            village: true,
            powerstation: true,
            hamlet: true,
            search: true,
        });
        setChangeCount(0);
        setPagination({ page: 0, limit: 100 });
        setBillingMonth("");

        setTimeout(
            () =>
                setClearAll({
                    district: false,
                    village: false,
                    powerstation: false,
                    hamlet: false,
                    search: false,
                }),
            0
        );
    };

    const { data, isFetching: isLoading } = useQuery(
        ["printBillHistory", pagination.page, pagination.limit, changeCount],
        () => getPrintBillHistory(pagination, filter),
        {
            enabled: changeCount > 0,
            onSuccess(res) {
                if (res) setRowsCount(res?.data.count);
            },
        }
    );

    const formattedData = useMemo(() => {
        if (!data?.data?.rows) return [];
        return data.data.rows.map((row: any) => ({
            ...row,
            meterNo: row?.meter?.meterNo || "-",
            operatorName: row?.operator?.name || "-",
            districtName: row?.meter?.district?.name || "-",
            villageName: row?.meter?.village?.name || "-",
            hamletName: row?.meter?.hamlet?.name || "-",
            powerstationName: row?.meter?.powerstation?.name || "-",
            createdAt: row?.createdAt ? new Date(row.createdAt).toLocaleString() : "-",
            billingMonth: (row?.year && row?.month)
                ? monthFormatter.format(new Date(row.year, row.month - 1, 1))
                : "-",
            id: row?.id,
        }));
    }, [data?.data?.rows]);

    useEffect(() => {
        if (billingMonth) {
            const [year, month] = billingMonth.split("-") ?? ["", ""];
            setFilter((prev: any) => ({ ...prev, year, month: parseInt(month) }));
        } else {
            setFilter((prev: any) => ({ ...prev, year: "", month: "" }));
        }
    }, [billingMonth]);

    useEffect(() => {
        if (
            filter.district &&
            filter.powerstation &&
            ![filter.district, filter.powerstation].includes("all")
        )
            return;

        setChangeCount(0);
    }, [filter.district, filter.powerstation]);

    return (
        <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Card elevation={0} sx={{ bgcolor: theme.palette.common.white, p: 2, mb: "1rem" }}>
                <Grid container>
                    <Grid item xs={12} md="auto">
                        <Typography variant="h5" color="GrayText">
                            Print Bill History
                        </Typography>
                    </Grid>
                </Grid>

                <Grid container spacing={1} mt={1}>
                    <MultiPermissionAuthorize ops={["READ DISTRICT"]}>
                        <Grid item xs={12} md={6} lg={4}>
                            <UpdatedSearchableInput
                                api="/region/district"
                                label="District"
                                filter={filter}
                                setFilter={setFilter}
                                clearAll={clearAll.district}
                            />
                        </Grid>
                    </MultiPermissionAuthorize>

                    <MultiPermissionAuthorize ops={["READ POWERSTATION"]}>
                        <Grid item xs={12} md={6} lg={4}>
                            <UpdatedSearchableInput
                                label="Powerstation"
                                api="/powerstation"
                                filter={filter}
                                setFilter={setFilter}
                                dep={filter.district}
                                params={{ district: filter.district && filter.district !== "all" ? filter.district : "" }}
                                clearAll={clearAll.powerstation}
                            />
                        </Grid>
                    </MultiPermissionAuthorize>

                    <MultiPermissionAuthorize ops={["READ VILLAGE"]}>
                        <Grid item xs={12} md={6} lg={4}>
                            <UpdatedSearchableInput
                                api="/region/village"
                                label="Village"
                                filter={filter}
                                setFilter={setFilter}
                                dep={[filter.district, filter.powerstation]}
                                params={{
                                    district: filter.district && filter.district !== "all" ? filter.district : "",
                                    powerstation: filter.powerstation && filter.powerstation !== "all" ? filter.powerstation : "",
                                }}
                                clearAll={clearAll.village}
                            />
                        </Grid>
                    </MultiPermissionAuthorize>

                    <MultiPermissionAuthorize ops={["READ HAMLET"]}>
                        <Grid item xs={12} md={6} lg={4}>
                            <UpdatedSearchableInput
                                api="/region/hamlet"
                                label="Hamlet"
                                filter={filter}
                                setFilter={setFilter}
                                dep={[filter.village, filter.powerstation]}
                                params={{
                                    village: filter.village,
                                    powerstation: filter.powerstation,
                                }}
                                clearAll={clearAll.hamlet}
                            />
                        </Grid>
                    </MultiPermissionAuthorize>

                    <Grid item xs={12} md={6} lg={4}>
                        <TextField
                            size="small"
                            name="billingMonth"
                            label="Billing month"
                            type="month"
                            fullWidth
                            value={billingMonth}
                            onChange={(e) => setBillingMonth(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            inputProps={{ max: currentMonth, min: "2025-10" }}
                            InputProps={{
                                endAdornment: billingMonth ? (
                                    <IconButton size="small" onClick={() => setBillingMonth("")}>
                                        <CloseOutlined />
                                    </IconButton>
                                ) : null,
                            }}
                        />
                    </Grid>

                    <Grid item xs={12} md={6} lg={4}>
                        <SearchBar filter={filter} setFilter={setFilter} clear={clearAll.search} />
                    </Grid>

                    <Grid item xs={12} md={6} lg={2}>
                        <Button
                            color="secondary"
                            variant="outlined"
                            onClick={() => {
                                setChangeCount((n) =>
                                    filter.search
                                        ? n + 1
                                        : [
                                            filter.district,
                                            filter.powerstation,
                                        ].some((x) => x === "all" || x === "")
                                            ? 0
                                            : n + 1
                                );
                            }}
                            sx={{ height: "100%" }}
                            startIcon={<FilterAltOutlined />}
                            fullWidth
                        >
                            Search
                        </Button>
                    </Grid>

                    <Grid item xs={12} md={6} lg={2}>
                        <Button
                            color="error"
                            variant="outlined"
                            startIcon={<FilterAltOffOutlined />}
                            onClick={handleClearAll}
                            sx={{ height: "100%", minWidth: theme.spacing(20) }}
                            fullWidth
                        >
                            Clear All
                        </Button>
                    </Grid>
                </Grid>
            </Card>

            <Box>
                {changeCount === 0 ? (
                    <Alert severity="info" sx={{ mt: "1rem" }}>
                        Please select "District" and "PowerStation" or enter "Meter No"
                    </Alert>
                ) : (
                    <BaseTable
                        headers={{
                            id: "ID",
                            meterNo: "Meter No",
                            districtName: "District",
                            villageName: "Village",
                            hamletName: "Hamlet",
                            powerstationName: "Powerstation",
                            billingMonth: "Billing Month",
                            operatorName: "Operator",
                            createdAt: "Created At",
                        }}
                        defaultSelectedHeaders={[
                            "id",
                            "meterNo",
                            "districtName",
                            "villageName",
                            "hamletName",
                            "powerstationName",
                            "billingMonth",
                            "operatorName",
                            "createdAt"
                        ]}
                        data={formattedData}
                        delEndPoint="/reading/print-bill-history"
                        feedback={feedback}
                        setFeedback={setFeedback}
                        rowsCount={rowsCount}
                        pagination={pagination}
                        setPagination={setPagination}
                        isLoading={isLoading}
                        permissions={{ delete: ["DELETE_BILL_HISTORY"], edit: ["NO EDIT"] }}
                        load={
                            (filter.search
                                ? filter.search !== ""
                                : Boolean(filter.district || filter.search) && filter.district !== "all") && changeCount > 0
                        }
                        queryKey={["printBillHistory", pagination.page, pagination.limit, changeCount]}
                        reportName="Print_Bill_History"
                    />
                )}
            </Box>
        </Box>
    );
};

export default PrintBillHistory;
